import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  SessionNote,
  CreateSessionNoteData,
  RBTDraft,
  AIEnhanced,
  SupervisorFinal,
  SupervisionData,
  Signature,
  EditHistoryEntry,
  SessionNoteStatus,
} from '../models/SessionNote';

class SessionNoteService {
  private collectionName = 'sessionNotes';

  /**
   * Create a new session note when appointment starts
   */
  async createSessionNote(data: CreateSessionNoteData, currentUserId: string): Promise<string> {
    const noteRef = doc(collection(db, this.collectionName));
    const noteId = noteRef.id;

    const now = Timestamp.now();

    const sessionNote: Record<string, any> = {
      sessionId: data.sessionId,
      appointmentId: data.appointmentId,
      rbtId: data.rbtId,
      rbtName: data.rbtName,
      rbtCredentials: data.rbtCredentials || '',
      supervisorId: data.supervisorId,
      supervisorName: data.supervisorName,
      supervisorCredentials: data.supervisorCredentials || '',
      clientId: data.clientId,
      companyId: data.companyId,
      timestamps: {
        sessionScheduled: now,
      },
      status: 'draft',
      createdAt: now,
      updatedAt: now,
      lastEditedBy: currentUserId,
      editHistory: [],
    };

    await setDoc(noteRef, sessionNote);
    return noteId;
  }

  /**
   * Save pre-session checklist
   */
  async savePreSessionChecklist(
    noteId: string,
    checkedItems: Record<string, boolean>,
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      preSessionChecklist: {
        completedAt: Timestamp.now(),
        items: checkedItems,
      },
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Save photo verification
   */
  async savePhotoVerification(
    noteId: string,
    photoUrl: string,
    location: { latitude: number; longitude: number },
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      photoVerification: {
        photoUrl,
        capturedAt: Timestamp.now(),
        location,
        verified: true,
      },
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Record session check-in with location
   */
  async recordCheckIn(
    noteId: string,
    location: { latitude: number; longitude: number; accuracy: number },
    currentUserId: string,
    locationVerification?: {
      isVerified: boolean;
      distance: number;
      requiresApproval: boolean;
      status: 'verified' | 'flagged' | 'approved' | 'rejected';
      appointmentAddress?: string;
      appointmentLatitude?: number;
      appointmentLongitude?: number;
    }
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    const updateData: any = {
      checkInLocation: location,
      'timestamps.sessionCheckIn': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    };

    // Add location verification data if provided
    if (locationVerification) {
      updateData.locationVerification = locationVerification;
    }

    await updateDoc(noteRef, updateData);
  }

  /**
   * Start session (begin timer)
   */
  async startSession(noteId: string, currentUserId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      'timestamps.sessionStart': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Privacy Mode: append an open privacy-pause window (compliance trail).
   */
  async logPrivacyPauseStart(noteId: string, userId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);
    const snap = await getDoc(noteRef);
    const pauses: any[] = (snap.data()?.privacyPauses as any[]) || [];
    pauses.push({ startTime: Timestamp.now(), endTime: null, byUserId: userId });
    await updateDoc(noteRef, { privacyPauses: pauses, updatedAt: serverTimestamp() });
  }

  /**
   * Privacy Mode: close the most recent open privacy-pause window.
   */
  async logPrivacyPauseEnd(noteId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);
    const snap = await getDoc(noteRef);
    const pauses: any[] = (snap.data()?.privacyPauses as any[]) || [];
    for (let i = pauses.length - 1; i >= 0; i--) {
      if (!pauses[i].endTime) {
        pauses[i].endTime = Timestamp.now();
        break;
      }
    }
    await updateDoc(noteRef, { privacyPauses: pauses, updatedAt: serverTimestamp() });
  }

  /**
   * Start video stream
   */
  async startStream(noteId: string, currentUserId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      'timestamps.streamStart': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * End session
   */
  async endSession(noteId: string, currentUserId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      'timestamps.sessionEnd': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * End video stream
   */
  async endStream(noteId: string, currentUserId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      'timestamps.streamEnd': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Submit RBT draft
   */
  async submitRBTDraft(
    noteId: string,
    draft: Omit<RBTDraft, 'submittedAt'>,
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    const rbtDraft: RBTDraft = {
      ...draft,
      submittedAt: Timestamp.now(),
    };

    // Add to edit history
    const editEntry: EditHistoryEntry = {
      timestamp: Timestamp.now(),
      userId: currentUserId,
      fieldChanged: 'rbtDraft',
      previousValue: 'null',
      newValue: 'Draft submitted',
    };

    await updateDoc(noteRef, {
      rbtDraft,
      'timestamps.rbtDraftSubmitted': Timestamp.now(),
      status: 'ai-processing',
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
      editHistory: [...(await this.getEditHistory(noteId)), editEntry],
    });
  }

  /**
   * Save AI-enhanced content
   */
  async saveAIEnhanced(
    noteId: string,
    aiEnhanced: Omit<AIEnhanced, 'generatedAt'>,
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    const aiContent: AIEnhanced = {
      ...aiEnhanced,
      generatedAt: Timestamp.now(),
    };

    await updateDoc(noteRef, {
      aiEnhanced: aiContent,
      'timestamps.aiProcessed': Timestamp.now(),
      status: 'supervisor-review',
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Start supervisor review
   */
  async startSupervisorReview(noteId: string, currentUserId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      'timestamps.supervisorReviewStarted': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Save supervisor's final notes
   */
  async saveSupervisorFinal(
    noteId: string,
    supervisorFinal: Omit<SupervisorFinal, 'editedAt'>,
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    const finalNotes: SupervisorFinal = {
      ...supervisorFinal,
      editedAt: Timestamp.now(),
    };

    const editEntry: EditHistoryEntry = {
      timestamp: Timestamp.now(),
      userId: currentUserId,
      fieldChanged: 'supervisorFinal',
      previousValue: 'AI enhanced',
      newValue: 'Supervisor edited',
    };

    await updateDoc(noteRef, {
      supervisorFinal: finalNotes,
      'timestamps.supervisorReviewCompleted': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
      editHistory: [...(await this.getEditHistory(noteId)), editEntry],
    });
  }

  /**
   * Save supervision compliance data
   */
  async saveSupervisionData(
    noteId: string,
    supervision: SupervisionData,
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      supervision,
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Add signature (RBT or Supervisor)
   */
  async addSignature(
    noteId: string,
    signatureType: 'rbt' | 'supervisor',
    signature: Omit<Signature, 'signedAt'>,
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    const sig: Signature = {
      ...signature,
      signedAt: Timestamp.now(),
    };

    const updateData: any = {
      [`signatures.${signatureType}`]: sig,
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    };

    // If supervisor is signing, finalize the document
    if (signatureType === 'supervisor') {
      updateData.status = 'approved';
      updateData['timestamps.documentFinalized'] = Timestamp.now();
    }

    await updateDoc(noteRef, updateData);
  }

  /**
   * Update session note with partial data
   */
  async updateSessionNote(
    noteId: string,
    updates: Partial<SessionNote>,
    currentUserId: string
  ): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      ...updates,
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Update session note status
   */
  async updateStatus(noteId: string, status: SessionNoteStatus, currentUserId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      status,
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Record PDF generation
   */
  async recordPDFGenerated(noteId: string, currentUserId: string): Promise<void> {
    const noteRef = doc(db, this.collectionName, noteId);

    await updateDoc(noteRef, {
      'timestamps.pdfGenerated': Timestamp.now(),
      updatedAt: serverTimestamp(),
      lastEditedBy: currentUserId,
    });
  }

  /**
   * Get a session note by ID
   */
  async getSessionNote(noteId: string): Promise<SessionNote | null> {
    const noteRef = doc(db, this.collectionName, noteId);
    const noteSnap = await getDoc(noteRef);

    if (!noteSnap.exists()) {
      return null;
    }

    return { id: noteSnap.id, ...noteSnap.data() } as SessionNote;
  }

  /**
   * Get session note by appointment ID
   */
  async getSessionNoteByAppointment(appointmentId: string, rbtId: string): Promise<SessionNote | null> {
    // Scoped to the caller's own note (owner branch) so it stays query-safe under
    // the tightened rules — this is the RBT looking up their own note at check-in.
    const q = query(
      collection(db, this.collectionName),
      where('appointmentId', '==', appointmentId),
      where('rbtId', '==', rbtId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as SessionNote;
  }

  /**
   * Get all session notes for an RBT
   */
  async getSessionNotesByRBT(rbtId: string, companyId: string): Promise<SessionNote[]> {
    // companyId pins the query to one facility so the supervisor/admin manager path
    // (querying another RBT's notes) is query-safe under the tightened rules. Two
    // equality filters + client-side sort avoids needing a new composite index.
    const q = query(
      collection(db, this.collectionName),
      where('rbtId', '==', rbtId),
      where('companyId', '==', companyId)
    );

    const snapshot = await getDocs(q);
    const notes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SessionNote));
    return notes.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  }

  /**
   * Get all session notes needing supervisor review
   */
  async getSessionNotesNeedingReview(supervisorId: string): Promise<SessionNote[]> {
    // Query without orderBy to avoid composite index requirement
    const q = query(
      collection(db, this.collectionName),
      where('supervisorId', '==', supervisorId),
      where('status', 'in', ['supervisor-review', 'ai-processing'])
    );

    const snapshot = await getDocs(q);
    const notes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SessionNote));

    // Sort by submission time in JavaScript (oldest first)
    return notes.sort((a, b) => {
      const aTime = a.timestamps?.rbtDraftSubmitted?.toDate?.()?.getTime() || 0;
      const bTime = b.timestamps?.rbtDraftSubmitted?.toDate?.()?.getTime() || 0;
      return aTime - bTime;
    });
  }

  /**
   * Get all approved session notes for a supervisor
   */
  async getApprovedSessionNotes(supervisorId: string): Promise<SessionNote[]> {
    const q = query(
      collection(db, this.collectionName),
      where('supervisorId', '==', supervisorId),
      where('status', '==', 'approved'),
      orderBy('timestamps.documentFinalized', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SessionNote));
  }

  /**
   * Get session notes for a client
   */
  async getSessionNotesByClient(clientId: string, rbtId: string): Promise<SessionNote[]> {
    // "Own notes only" policy: the requesting RBT's own notes for this client
    // (owner branch → query-safe). Sorting done client-side.
    const q = query(
      collection(db, this.collectionName),
      where('clientId', '==', clientId),
      where('rbtId', '==', rbtId)
    );

    const snapshot = await getDocs(q);
    const notes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SessionNote));

    // Sort client-side by createdAt descending
    return notes.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
  }

  /**
   * Get edit history for a session note
   */
  private async getEditHistory(noteId: string): Promise<EditHistoryEntry[]> {
    const note = await this.getSessionNote(noteId);
    return note?.editHistory || [];
  }
}

export const sessionNoteService = new SessionNoteService();
